import { c as _c } from "react/compiler-runtime";
import { SphereAvatar } from "@components/header/sphere-avatar";
import DateAdapter from "@date-io/luxon";
import { ChipStatusText } from "@flat-ui";
import { AnnotationStatus } from "@faro-lotv/ielement-types";
import { sortAssigneeMembers } from "@pages/project-details/project-markups/markup-assignee-utils";
import { CURRENT_USER_SEARCH_TERM, ProjectMarkupsTableHeaders } from "@pages/project-details/project-markups/project-markups-table-utils";
import { MarkupStatusIcon } from "@pages/project-details/project-markups/status/markup-status-icon";
import { getMarkupStatusValue } from "@pages/project-details/project-markups/status/markup-status-utils";
import { markupsUniqueAssigneeSelector, markupsUniqueCreatorsSelector, markupsUniqueKeysSelector } from "@store/markups/markups-selector";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { getPrettyName, getUserInitials } from "@utils/user-utils";
import { useMemo } from "react";

// Created once, so that every component using the filters shares the memoized result
const markupsLabelsSelector = markupsUniqueKeysSelector("labels");
const markupsAssigneesSelector = markupsUniqueAssigneeSelector();

/** Returns all the available filters for ProjectMarkup */
export function useProjectMarkupsFilterLists() {
  const $ = _c(15);
  const markupsLabels = useAppSelector(markupsLabelsSelector);
  const markupsAssignees = useAppSelector(markupsAssigneesSelector);
  const markupsCreators = useAppSelector(markupsUniqueCreatorsSelector);
  const currentUser = useAppSelector(currentUserSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getMarkupsStatusFilterOptions();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== markupsLabels) {
    t1 = getMarkupsTagsFilterOptions(markupsLabels);
    $[1] = markupsLabels;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getMarkupsAttachmentFilterOptions();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currentUser || $[5] !== markupsAssignees) {
    t3 = getMarkupsAssigneesFilterOptions(withMember(markupsAssignees, currentUser), currentUser?.identity);
    $[4] = currentUser;
    $[5] = markupsAssignees;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== currentUser || $[8] !== markupsCreators) {
    t4 = getMarkupsMemberFilterOptions(withMember(markupsCreators, currentUser), _temp, currentUser?.identity);
    $[7] = currentUser;
    $[8] = markupsCreators;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = getMarkupsDueDateFilterOptions();
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t3 || $[13] !== t4) {
    t6 = {
      [ProjectMarkupsTableHeaders.status]: t0,
      [ProjectMarkupsTableHeaders.tags]: t1,
      [ProjectMarkupsTableHeaders.attachments]: t2,
      [ProjectMarkupsTableHeaders.assignee]: t3,
      [ProjectMarkupsTableHeaders.creator]: t4,
      [ProjectMarkupsTableHeaders.dueDate]: t5
    };
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const allFilters = t6;
  return allFilters;
}

/** Returns the members with the given one too, so the current user can always filter by themselves */
function _temp(markup) {
  return markup.createdBy;
}
function withMember(members, member) {
  return !member || members.some(({
    identity
  }) => identity === member.identity) ? members : [...members, member];
}
function getMarkupsStatusFilterOptions() {
  const noStatusFilter = {
    unclassified: {
      id: AnnotationStatus.Unclassified,
      hasEntityPassedFilter: markup => getMarkupStatusValue(markup.status?.value) === AnnotationStatus.Unclassified,
      label: ChipStatusText.unclassified,
      icon: <MarkupStatusIcon key={AnnotationStatus.Unclassified} status={AnnotationStatus.Unclassified} shouldHaveBorder={true} />
    }
  };
  const statusFilters = Object.values(AnnotationStatus).reduce((acc, value) => {
    return {
      ...acc,
      [value]: {
        id: value,
        hasEntityPassedFilter: markup => getMarkupStatusValue(markup.status?.value) === value,
        label: ChipStatusText[value],
        icon: <MarkupStatusIcon key={value} status={value} shouldHaveBorder={true} />
      }
    };
  }, {});
  return {
    ...noStatusFilter,
    ...statusFilters
  };
}

/** Returns all the filter options for markup tags */
function getMarkupsTagsFilterOptions(markupsLabels) {
  const untaggedFilter = {
    untagged: {
      id: "untagged",
      label: "Untagged",
      hasEntityPassedFilter: markup => !markup.labels || markup.labels.length === 0
    }
  };
  const tagFilters = markupsLabels.flat().reduce((acc, label) => {
    return {
      ...acc,
      [label.id]: {
        id: label.id,
        label: label.name,
        hasEntityPassedFilter: markup => {
          const tags = Array.isArray(markup.labels) ? markup.labels.map(label => label.id) : undefined;
          return tags?.includes(label.id) ?? false;
        }
      }
    };
  }, {});
  return {
    ...untaggedFilter,
    ...tagFilters
  };
}

/** Returns all the filter options for markup attachments */
function getMarkupsAttachmentFilterOptions() {
  return {
    hasAttachment: {
      id: "hasAttachment",
      hasEntityPassedFilter: markup => markup.attachmentsCount > 0,
      label: ""
    }
  };
}

/** Returns all the filter options for markup assignee */
function getMarkupsAssigneesFilterOptions(markupsAssignees, currentUserId) {
  const noAssigneeFilter = {
    unassigned: {
      id: "unassigned",
      label: "Unassigned",
      hasEntityPassedFilter: markup => markup.assignee?.values?.[0] === undefined
    }
  };
  const assigneeFilters = getMarkupsMemberFilterOptions(markupsAssignees, markup => markup.assignee?.values?.[0], currentUserId);
  return {
    ...noAssigneeFilter,
    ...assigneeFilters
  };
}

/** Returns a filter option for each member, matching the markups whose member returned by the callback is them */
function getMarkupsMemberFilterOptions(members, getMarkupMemberId, currentUserId) {
  return sortAssigneeMembers(members, currentUserId).reduce((acc, member) => {
    return {
      ...acc,
      [member.identity]: {
        id: member.identity,
        label: getPrettyName(member),
        searchTerms: [member.email, ...(currentUserId === member.identity ? [CURRENT_USER_SEARCH_TERM] : [])],
        hasEntityPassedFilter: markup => getMarkupMemberId(markup) === member.identity,
        icon: <SphereAvatar shouldUseSimplifiedAvatar={currentUserId !== member.identity} size={"small"} initials={getUserInitials(member) ?? ""} src={member.thumbnailUrl} />
      }
    };
  }, {});
}

/** Returns all the filter options for markup due date */
export function getMarkupsDueDateFilterOptions() {
  const dateAdapter = new DateAdapter();
  const today = dateAdapter.date();
  const startOfToday = dateAdapter.startOfDay(today);
  const endOfToday = dateAdapter.endOfDay(today);

  /* eslint-disable @typescript-eslint/no-magic-numbers -- these numbers are requited for giving duration */
  return {
    beforeToday: {
      id: "beforeToday",
      label: "Before today",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate < startOfToday.valueOf();
      }
    },
    today: {
      id: "today",
      label: "Today",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= startOfToday.valueOf() && dueDate < endOfToday.valueOf();
      }
    },
    tomorrow: {
      id: "tomorrow",
      label: "Tomorrow",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= dateAdapter.addDays(startOfToday, 1).valueOf() && dueDate < dateAdapter.addDays(startOfToday, 2).valueOf();
      }
    },
    next7Days: {
      id: "next7Days",
      label: "Next 7 days",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= dateAdapter.addDays(startOfToday, 0).valueOf() && dueDate < dateAdapter.addDays(startOfToday, 7).valueOf();
      }
    },
    next30Days: {
      id: "next30Days",
      label: "Next 30 days",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= dateAdapter.addDays(startOfToday, 0).valueOf() && dueDate < dateAdapter.addDays(startOfToday, 30).valueOf();
      }
    },
    next90Days: {
      id: "next90Days",
      label: "Next 90 days",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value ? dateAdapter.date(markup.dueDate.value)?.valueOf() : undefined;
        return dueDate !== undefined && dueDate >= dateAdapter.addDays(startOfToday, 0).valueOf() && dueDate < dateAdapter.addDays(startOfToday, 90).valueOf();
      }
    },
    noDate: {
      id: "noDate",
      label: "No date",
      hasEntityPassedFilter: markup => {
        const dueDate = markup.dueDate?.value;
        return dueDate === undefined;
      }
    }
  };
  /* eslint-enable @typescript-eslint/no-magic-numbers */
}